/**
 * Typed testid registry for the `integrations` domain — the SINGLE source of
 * truth for every `data-testid` this domain renders (TV1-2055, rolling out the
 * TV1-1866 pattern to integrations after phase 1).
 *
 * Components spread these values into `data-testid`; the LIVE Playwright specs
 * (`src/tests/e2e/integrations/setup.spec.ts`,
 * `src/tests/e2e/integrations/category-mapper/*`) import the SAME object for
 * their selectors. A renamed key changes the string in ONE place and both sides
 * move together — instead of a runtime "element not found" when a component
 * testid drifts from the string a spec hardcoded (e2e is not run in CI, so that
 * drift fails silently). The `teselly/no-raw-testid` ESLint rule forbids raw
 * `data-testid` literals in this domain so new testids must land here.
 *
 * Naming convention: `integrations-[section]-[element]` in kebab-case (see
 * `.claude/rules/e2e-testing.md`). NOTE: many pre-existing literals in this
 * domain predate that convention (`sales-channel-setup-*`, `add-invoicing-*`,
 * `invoicing-*`, `logistics-tab`, `channel-*`). Values are preserved VERBATIM
 * from those literals (no rename) so the migration is value-safe; only the keys
 * are new. Per-row / per-connector ids are built by the factory functions below,
 * which reproduce the exact interpolated string shape.
 */
export const integrationsTestIds = {
  // --- org-wide revoked-integrations banner (TV1-2446 slice 7) ---
  /** The banner root, rendered above `<UserNavbar/>` while at least one integration is revoked. */
  revokedIntegrationsBanner: 'integrations-revoked-banner',
  /** One revoked-integration row within the banner (keyed by integration id). */
  revokedIntegrationsBannerItem: ( integrationId: string ): string =>
    `integrations-revoked-banner-item-${ integrationId }`,
  /** The "Reconnect now" CTA on a row, shown only to `sales-channels:integrations:configure` holders. */
  revokedIntegrationsBannerReconnect: ( integrationId: string ): string =>
    `integrations-revoked-banner-reconnect-${ integrationId }`,
  /** The no-permission copy shown to members who cannot reconnect. */
  revokedIntegrationsBannerNoPermission: ( integrationId: string ): string =>
    `integrations-revoked-banner-no-permission-${ integrationId }`,

  // --- channel detail: credential revocation (TV1-2446 slice 6) ---
  /** The channel detail's "Import" button (disabled while the credential is revoked). */
  channelInfoImportButton: 'integrations-channel-info-import-button',
  /** Inline notice explaining why an action is disabled on a revoked channel. */
  channelCredentialRevokedNotice: 'integrations-channel-credential-revoked-notice',
  /** The notice's Reconnect call to action (a link to the channel detail page, or on that page the Reconnect action itself). */
  channelCredentialRevokedReconnect: 'integrations-channel-credential-revoked-reconnect',
  /** The channel detail header's Reconnect button, shown only while the credential is revoked (slice 5). */
  channelInfoReconnectButton: 'integrations-channel-info-reconnect-button',

  // --- sales-channel setup wizard: shell ---
  /** Sales-channel setup wizard root. */
  /* TV1-2365 — the inline checklist that replaced the wizard's auto-open. */
  channelSetupAlert: 'channels-setup-alert',
  channelSetupChecklist: 'channels-setup-checklist',
  channelSetupChecklistItem: ( item: string ) => `channels-setup-checklist-item-${ item }`,
  channelSetupChecklistSave: ( item: string ) => `channels-setup-checklist-save-${ item }`,

  // --- sales-channel setup wizard: logistics step ---
  /** Logistics-connector option row in the wizard (keyed by option value). */
  channelSetupLogisticsConnectorOption: ( value: string ): string =>
    `sales-channel-setup-logistics-connector-${ value }`,
  /** Logistics integration name input. */
  channelSetupLogisticsNameInput: 'sales-channel-setup-logistics-name-input',
  /** Logistics step form container. */
  channelSetupLogisticsForm: 'sales-channel-setup-logistics-form',
  /** Logistics mode selector. */
  channelSetupLogisticsMode: 'sales-channel-setup-logistics-mode',
  /** Existing-logistics-integration selector. */
  channelSetupLogisticsIntegration: 'sales-channel-setup-logistics-integration',
  /** Logistics step trigger (advance / submit). */
  channelSetupLogisticsTrigger: 'sales-channel-setup-logistics-trigger',
  /** Logistics step status indicator. */
  channelSetupLogisticsStatus: 'sales-channel-setup-logistics-status',
  /** Logistics connector selector. */
  channelSetupLogisticsConnector: 'sales-channel-setup-logistics-connector',
  /** Register-new-logistics action. */
  channelSetupLogisticsRegister: 'sales-channel-setup-logistics-register',

  // --- sales-channel setup wizard: invoicing provision modal ---
  /** Invoicing-connector option row in the provision modal (keyed by option value). */
  channelSetupInvoicingConnectorOption: ( value: string ): string =>
    `sales-channel-setup-invoicing-connector-${ value }`,
  /** Invoicing cert PEM input. */
  channelSetupInvoicingCertPemInput: 'sales-channel-setup-invoicing-cert-pem-input',
  /** Invoicing key PEM input. */
  channelSetupInvoicingKeyPemInput: 'sales-channel-setup-invoicing-key-pem-input',
  /** Invoicing CUIT input. */
  channelSetupInvoicingCuitInput: 'sales-channel-setup-invoicing-cuit-input',
  /** Invoicing production-mode checkbox. */
  channelSetupInvoicingProductionCheckbox: 'sales-channel-setup-invoicing-production-checkbox',
  /** Invoicing provision modal root. */
  channelSetupInvoicingModal: 'sales-channel-setup-invoicing-modal',
  /** Invoicing connector selector. */
  channelSetupInvoicingConnector: 'sales-channel-setup-invoicing-connector',
  /** Invoicing integration name input. */
  channelSetupInvoicingNameInput: 'sales-channel-setup-invoicing-name-input',
  /** Invoicing connect action. */
  channelSetupInvoicingConnect: 'sales-channel-setup-invoicing-connect',

  // --- sales-channel setup wizard: inventory step ---
  /** Inventory step form container. */
  channelSetupInventoryForm: 'sales-channel-setup-inventory-form',
  /** Inventory warehouse selector. */
  channelSetupInventoryWarehouse: 'sales-channel-setup-inventory-warehouse',
  // TV1-2354: one-shot publication import toggle on the setup wizard.
  channelSetupAutoImportPublications: 'channel-setup-auto-import-publications',
  // TV1-2379: one-shot historical order import toggle on the setup wizard.
  channelSetupAutoImportOrders: 'channel-setup-auto-import-orders',
  /** Channel-stock explanatory note. */
  channelSetupInventoryChannelStockNote: 'sales-channel-setup-inventory-channel-stock-note',
  /** Inventory step trigger. */
  channelSetupInventoryTrigger: 'sales-channel-setup-inventory-trigger',
  /** Inventory step status indicator. */
  channelSetupInventoryStatus: 'sales-channel-setup-inventory-status',

  // --- sales-channel setup wizard: billing step ---
  /** Billing step form container. */
  channelSetupBillingForm: 'sales-channel-setup-billing-form',
  /** Billing mode selector. */
  channelSetupBillingMode: 'sales-channel-setup-billing-mode',
  /** Billing entity selector. */
  channelSetupBillingEntity: 'sales-channel-setup-billing-entity',
  /** Billing step trigger. */
  channelSetupBillingTrigger: 'sales-channel-setup-billing-trigger',
  /** Billing step status indicator. */
  channelSetupBillingStatus: 'sales-channel-setup-billing-status',
  /** Billing invoicing-integration selector. */
  channelSetupBillingInvoicingIntegration: 'sales-channel-setup-billing-invoicing-integration',
  /** Add-invoicing action inside the billing step. */
  channelSetupBillingAddInvoicing: 'sales-channel-setup-billing-add-invoicing',
  /** Billing create action. */
  channelSetupBillingCreate: 'sales-channel-setup-billing-create',

  // --- connector list + capability-aware setup panels ---
  /**
   * Connector list row (keyed by connector id). Shared by `ConnectorList` (via
   * `connector.id`) and `AddChannelModal` (via `option.value`) — identical shape.
   */
  connectorRow: ( id: string ): string => `integrations-connector-row-${ id }`,
  /** Capability-grouped connector section (keyed by section kind). */
  connectorSection: ( kind: string ): string => `integrations-section-${ kind }`,
  /** Server-configured connector read-only panel (keyed by connector id). */
  connectorServerConfigured: ( connectorId: string ): string =>
    `integrations-connector-serverConfigured-${ connectorId }`,
  /** OAuth panel store-URL input (keyed by connector id). */
  setupOauthStoreUrl: ( connectorId: string ): string =>
    `integrations-setup-oauth-storeUrl-${ connectorId }`,
  /** Api-key panel: the primary key/secret credential input. */
  setupApikeyKeyInput: 'integrations-setup-apikey-key-input',
  /** Api-key panel: a non-secret credential input (keyed by field name). */
  setupApikeyFieldInput: ( fieldName: string ): string =>
    `integrations-setup-apikey-${ fieldName }-input`,
  /** Direct-provision panel root (keyed by connector id). */
  setupDirectProvision: ( connectorId: string ): string =>
    `integrations-setup-directProvision-${ connectorId }`,
  /** Direct-provision panel name input (keyed by connector id). */
  setupDirectProvisionNameInput: ( connectorId: string ): string =>
    `integrations-setup-directProvision-nameInput-${ connectorId }`,
  /** Direct-provision panel config row (keyed by connector id). */
  setupDirectProvisionConfigRow: ( connectorId: string ): string =>
    `integrations-setup-directProvision-configRow-${ connectorId }`,

  // --- erps ---
  /**
   * Add-ERP empty card. The only one of the four integrations add-cards that
   * had no testid at all until TV1-2294 #43 — channels, logistics and invoicing
   * each carried one, so the ERPs section was the odd one out rather than a
   * deliberate omission.
   */
  erpsAddCard: 'integrations-erps-add-card',
  /** ERP add-modal continue button. */
  erpsContinueButton: 'integrations-erps-continue-button',
  /**
   * ERP configuration view: the content panel of a tab (keyed by tab value, as
   * registered in `ERPInfo/erpInfoTabs.tsx`). One id per tab, so a new tab needs
   * no new entry here — it inherits the panel id from its registered value.
   */
  erpInfoTabPanel: ( tab: string ): string => `integrations-erp-info-panel-${ tab }`,
  /** ERP configuration view, Items tab: the "import the catalogue" action. */
  erpItemsImportButton: 'integrations-erp-items-import-button',
  /** ERP configuration view, Warehouses tab: the container-discovery spinner. */
  erpWarehousesLoading: 'integrations-erp-warehouses-loading',
  /** ERP configuration view, Warehouses tab: "the ERP reports no warehouses". */
  erpWarehousesEmpty: 'integrations-erp-warehouses-empty',
  /** ERP configuration view, Warehouses tab: the discovered-container table. */
  erpWarehousesTable: 'integrations-erp-warehouses-table',
  /** One discovered ERP warehouse row (keyed by the ERP's own warehouse id). */
  erpWarehouseRow: ( externalWarehouseId: string ): string =>
    `integrations-erp-warehouse-row-${ externalWarehouseId }`,
  /** Per-row create/link/ignore selector (keyed by the ERP's own warehouse id). */
  erpWarehouseActionSelect: ( externalWarehouseId: string ): string =>
    `integrations-erp-warehouse-action-${ externalWarehouseId }`,
  /** Per-row "link to this Teselly warehouse" selector (keyed by the ERP's warehouse id). */
  erpWarehouseLinkSelect: ( externalWarehouseId: string ): string =>
    `integrations-erp-warehouse-link-${ externalWarehouseId }`,
  /** "Already mapped" badge on a discovered row (keyed by the ERP's warehouse id). */
  erpWarehouseMappedBadge: ( externalWarehouseId: string ): string =>
    `integrations-erp-warehouse-mapped-${ externalWarehouseId }`,
  /**
   * "Sugerido" badge on a row the deterministic matcher PRE-FILLED (keyed by the ERP's
   * warehouse id). Load-bearing, not decoration: a proposal must be visibly a proposal,
   * so an operator never mistakes a pre-fill for their own earlier choice.
   */
  erpWarehouseSuggestedBadge: ( externalWarehouseId: string ): string =>
    `integrations-erp-warehouse-suggested-${ externalWarehouseId }`,
  /** ERP configuration view, Warehouses tab: the confirm-the-selection action. */
  erpWarehousesConfirmButton: 'integrations-erp-warehouses-confirm-button',
  /** ERP configuration view, Price lists tab: the container-discovery spinner. */
  erpPriceListsLoading: 'integrations-erp-price-lists-loading',
  /** ERP configuration view, Price lists tab: "the ERP reports no pricelists". */
  erpPriceListsEmpty: 'integrations-erp-price-lists-empty',
  /** ERP configuration view, Price lists tab: the discovered-container table. */
  erpPriceListsTable: 'integrations-erp-price-lists-table',
  /** One discovered ERP pricelist row (keyed by the ERP's own pricelist id). */
  erpPriceListRow: ( externalPricelistId: string ): string =>
    `integrations-erp-price-list-row-${ externalPricelistId }`,
  /** Per-row create/link/ignore selector (keyed by the ERP's own pricelist id). */
  erpPriceListActionSelect: ( externalPricelistId: string ): string =>
    `integrations-erp-price-list-action-${ externalPricelistId }`,
  /** Per-row "link to this Teselly price list" selector (keyed by the ERP's pricelist id). */
  erpPriceListLinkSelect: ( externalPricelistId: string ): string =>
    `integrations-erp-price-list-link-${ externalPricelistId }`,
  /** "Already mapped" badge on a discovered row (keyed by the ERP's pricelist id). */
  erpPriceListMappedBadge: ( externalPricelistId: string ): string =>
    `integrations-erp-price-list-mapped-${ externalPricelistId }`,
  /**
   * "The ERP reports no currency for this pricelist" warning (keyed by the ERP's
   * pricelist id). The row cannot be CREATED — only linked or ignored — and saying so
   * in the list is what keeps the server's refusal from reading as a bug.
   */
  erpPriceListNoCurrencyWarning: ( externalPricelistId: string ): string =>
    `integrations-erp-price-list-no-currency-${ externalPricelistId }`,
  /** ERP configuration view, Price lists tab: the confirm-the-selection action. */
  erpPriceListsConfirmButton: 'integrations-erp-price-lists-confirm-button',
  /** ERP configuration view, Stock tab: the pre-confirm projection spinner. */
  erpStockLoading: 'integrations-erp-stock-loading',
  /** ERP configuration view, Stock tab: the projection block. */
  erpStockProjection: 'integrations-erp-stock-projection',
  /**
   * The projected VOLUME line — "N items x M warehouses = R rows" (O11). Its own testid
   * because it is the one figure the operator is meant to read before confirming: it is
   * what turns a 5,000-SKU job into a 15,000-row one.
   */
  erpStockProjectedRows: 'integrations-erp-stock-projected-rows',
  /**
   * "No ERP warehouse is mapped" — the state in which the sweep is REFUSED rather than
   * run. The server refuses it too; this keeps the button from being offered at all.
   */
  erpStockNothingMappedWarning: 'integrations-erp-stock-nothing-mapped',
  /**
   * "N ERP warehouses are still unmapped" — the PARTIAL case (O12), which is the
   * damaging one: the run is completely correct over a fraction of the depots, under a
   * green bar. Stated, never hidden.
   */
  erpStockPartialMappingWarning: 'integrations-erp-stock-partial-mapping',
  /** ERP configuration view, Stock tab: the "import stock" action. */
  erpStockImportButton: 'integrations-erp-stock-import-button',
  /** ERP configuration view, Prices tab: the pre-confirm projection spinner. */
  erpPricesLoading: 'integrations-erp-prices-loading',
  /** ERP configuration view, Prices tab: the projection block. */
  erpPricesProjection: 'integrations-erp-prices-projection',
  /**
   * The projected VOLUME line — "R prices across M price lists" (O11). Its own testid
   * because it is the one figure the operator is meant to read before confirming:
   * importing every Odoo pricelist multiplies the catalogue by the number of price lists.
   */
  erpPricesProjectedRows: 'integrations-erp-prices-projected-rows',
  /**
   * "No ERP price list is mapped" — the state in which the sweep is REFUSED rather than
   * run. The server refuses it too; this keeps the button from being offered at all.
   */
  erpPricesNothingMappedWarning: 'integrations-erp-prices-nothing-mapped',
  /**
   * "N ERP price lists are still unmapped" — the PARTIAL case (O12), which is the
   * damaging one: the run is completely correct over a fraction of the seller's prices,
   * under a green bar. Stated, never hidden.
   */
  erpPricesPartialMappingWarning: 'integrations-erp-prices-partial-mapping',
  /** ERP configuration view, Prices tab: the "import prices" action. */
  erpPricesImportButton: 'integrations-erp-prices-import-button',

  // --- invoicing ---
  /** Invoicing tab wrapper. */
  invoicingTab: 'invoicing-tab',
  /** Invoicing info panel (both the empty and populated render branches). */
  invoicingInfo: 'invoicing-info',
  /** Invoicing list grid. */
  invoicingList: 'invoicing-list',
  /** Add-invoicing empty card. */
  addInvoicingCard: 'add-invoicing-card',
  /** Add-invoicing connector option row (keyed by option value). */
  addInvoicingConnectorOption: ( value: string ): string =>
    `add-invoicing-connector-${ value }`,
  /** Add-invoicing cert PEM input. */
  addInvoicingCertPemInput: 'add-invoicing-cert-pem-input',
  /** Add-invoicing key PEM input. */
  addInvoicingKeyPemInput: 'add-invoicing-key-pem-input',
  /** Add-invoicing CUIT input. */
  addInvoicingCuitInput: 'add-invoicing-cuit-input',
  /** Add-invoicing production-mode checkbox. */
  addInvoicingProductionCheckbox: 'add-invoicing-production-checkbox',
  /** Add-invoicing modal root (both the connector-select and form render branches). */
  addInvoicingModal: 'add-invoicing-modal',
  /** Add-invoicing name input. */
  addInvoicingNameInput: 'add-invoicing-name-input',
  /** Add-invoicing submit button. */
  addInvoicingSubmit: 'add-invoicing-submit',

  // --- logistics ---
  /** Logistics tab wrapper. */
  logisticsTab: 'logistics-tab',
  /** Logistics list grid. */
  logisticsList: 'integrations-logistics-list',
  /** Logistics info panel (both the empty and populated render branches). */
  logisticsInfo: 'integrations-logistics-info',
  /** Add-logistics empty card. */
  logisticsAddCard: 'integrations-logistics-add-card',
  /** Logistics card in the list (keyed by integration id). */
  logisticsCard: ( id: string ): string => `integrations-logistics-card-${ id }`,
  /** Logistics info: open-test-channel action. */
  logisticsOpenTestChannel: 'integrations-logistics-open-test-channel',
  /** Logistics info: poll-shipments action. */
  logisticsPollShipments: 'integrations-logistics-poll-shipments',
  /** Add-logistics connector option row (keyed by option value). */
  addLogisticsConnectorOption: ( value: string ): string =>
    `integrations-logistics-connector-${ value }`,
  /** Add-logistics name input. */
  addLogisticsNameInput: 'integrations-logistics-name-input',
  /** Add-logistics modal root (both the connector-select and form render branches). */
  addLogisticsModal: 'integrations-logistics-modal',
  /** Add-logistics connector selector. */
  addLogisticsConnectorSelect: 'integrations-logistics-connector-select',
  /** Add-logistics submit button. */
  addLogisticsSubmitButton: 'integrations-logistics-submit-button',

  // --- messaging (TV1-2335, "Comunicaciones" tab) ---
  /** Messaging tab wrapper. */
  messagingTab: 'integrations-messaging-tab',
  /** Messaging list grid. */
  messagingList: 'integrations-messaging-list',
  /** Messaging info panel (both the empty and populated render branches). */
  messagingInfo: 'integrations-messaging-info',
  /** Messaging info: per-account integration lifecycleState badge. */
  messagingLifecycleBadge: 'integrations-messaging-lifecycle-badge',
  /** Add-messaging empty card. */
  messagingAddCard: 'integrations-messaging-add-card',
  /** Messaging card in the list (keyed by integration id). */
  messagingCard: ( id: string ): string => `integrations-messaging-card-${ id }`,
  /** Add-messaging connector option row (keyed by option value). */
  addMessagingConnectorOption: ( value: string ): string =>
    `integrations-messaging-connector-${ value }`,
  /** Add-messaging name input (direct-provision branch only). */
  addMessagingNameInput: 'integrations-messaging-name-input',
  /** Add-messaging modal root (both the connector-select and form render branches). */
  addMessagingModal: 'integrations-messaging-modal',
  /** Add-messaging connector selector. */
  addMessagingConnectorSelect: 'integrations-messaging-connector-select',
  /** Add-messaging SMTP "secure" (implicit TLS) switch. */
  addMessagingSecureSwitch: 'integrations-messaging-secure-switch',
  /** Add-messaging submit button. */
  addMessagingSubmitButton: 'integrations-messaging-submit-button',

  // --- channels: list + add ---
  /** Channel card in the list (keyed by channel id). */
  channelItem: ( id: string ): string => `integrations-item-${ id }`,
  // TV1-2361: the card's completeness bar and its status line.
  channelCardRing: ( id: string ) => `channel-card-ring-${id}`,
  channelCardBar: ( id: string ) => `channel-card-bar-${id}`,
  channelCardStatus: ( id: string ) => `channel-card-status-${id}`,
  /** Add-channel button. */
  channelsAddButton: 'integrations-channels-add-button',
  channelsEmptyState: 'channels-empty-state',
  /** Add-channel modal continue button. */
  channelsContinueButton: 'integrations-channels-continue-button',
  // TV1-2354: auto-import toggle offered while connecting a channel.
  addChannelAutoImportPublications: 'add-channel-auto-import-publications',
  // TV1-2379: historical order import toggle offered while connecting a channel.
  addChannelAutoImportOrders: 'add-channel-auto-import-orders',

  // --- channels: detail (ChannelInfo) ---
  /** Channel-info run-setup action. */
  channelInfoRunSetup: 'channels-info-run-setup',
  /** Channel-info "Categorías" tab content card. */
  channelInfoTabCategories: 'channel-info-tab-categories',

  // --- channels: status mapping ---
  /** Status-mapping tab wrapper. */
  statusMappingTab: 'channel-status-mapping-tab',
  /** Status-mapping row (keyed by row index). */
  statusMappingRow: ( index: number ): string =>
    `channel-status-mapping-row-${ index }`,
  /** Status-mapping add-row action. */
  statusMappingAdd: 'channel-status-mapping-add',
  /** Status-mapping save action. */
  statusMappingSave: 'channel-status-mapping-save',

  // --- channels: category mapper ---
  /** Category row (keyed by external category id). */
  channelCategoryRow: ( externalId: string ): string =>
    `channel-category-row-${ externalId }`,
  /** Category mapping cell (keyed by external category id). */
  channelCategoryMapping: ( externalId: string ): string =>
    `channel-category-mapping-${ externalId }`,
  /** Auto-mapped (exact-match) badge (keyed by external category id). */
  channelCategoryAutoBadge: ( externalId: string ): string =>
    `channel-category-auto-badge-${ externalId }`,
  /** AI-mapped badge — model-proposed, human-approved (keyed by external category id). */
  channelCategoryAiBadge: ( externalId: string ): string =>
    `channel-category-ai-badge-${ externalId }`,
  /** "No aplica — agrupación comercial" badge (keyed by external category id). */
  channelCategoryUnmappableBadge: ( externalId: string ): string =>
    `channel-category-unmappable-badge-${ externalId }`,
  /** "Map it anyway" override on an unmappable row (keyed by external category id). */
  channelCategoryMapAnyway: ( externalId: string ): string =>
    `channel-category-map-anyway-${ externalId }`,
  /** Unmap action on a mapped row (keyed by external category id). */
  channelCategoryUnmap: ( externalId: string ): string =>
    `channel-category-unmap-${ externalId }`,
  /** Suggestion chip (keyed by external category id + suggested Teselly category id). */
  channelCategorySuggestion: (
    externalId: string,
    tesellyCategoryId: string
  ): string =>
    `channel-category-suggestion-${ externalId }-${ tesellyCategoryId }`,
  /** Manual-search picker (keyed by external category id). */
  channelCategorySearch: ( externalId: string ): string =>
    `channel-category-search-${ externalId }`,
  /** Category mapper resync button. */
  categoriesResyncButton: 'channel-categories-resync-button',
  /** "Mapear con IA" launcher — proposes the `map-channel-categories` operation. */
  categoriesAiMapButton: 'channel-categories-ai-map-button',
  /* TV1-2366 — the run is proposed, watched and reviewed on this screen. */
  categoriesAiRunBand: 'channel-categories-ai-run-band',
  categoriesPendingAlert: 'channel-categories-pending-alert',
  channelCategoryEdit: ( externalId: string ): string =>
    `channel-category-edit-${ externalId }`,
  channelCategoryAcceptSuggestion: ( externalId: string ): string =>
    `channel-category-accept-suggestion-${ externalId }`,
  channelCategoryDiscardSuggestion: ( externalId: string ): string =>
    `channel-category-discard-suggestion-${ externalId }`,
  /** Banner shown when an AI mapping run for this channel is awaiting review. */
  categoriesAiReviewBanner: 'channel-categories-ai-review-banner',
  /** Banner action that navigates to the operation review surface. */
  categoriesAiReviewBannerAction: 'channel-categories-ai-review-banner-action',
  /** Outstanding-work counter (mapped rows and unmappable rows excluded). */
  categoriesPendingCount: 'channel-categories-pending-count',
  /** Category mapper empty state. */
  categoriesEmpty: 'channel-categories-empty',
  /** Category mapper list container. */
  categoriesList: 'channel-categories-list',
  /** Sort control for the category mapper list. */
  categoriesSortSelect: 'channel-categories-sort-select',
  /** Mapping-status multi-select filter. */
  categoriesMappingStatusFilter: 'channel-categories-mapping-status-filter',
  /** List search input (channel category name/path). */
  categoriesListSearch: 'channel-categories-list-search',
  /** Clear list filters action. */
  categoriesClearFilters: 'channel-categories-clear-filters',
  /** Filtered-empty state when search/status filters hide every row. */
  categoriesFilteredEmpty: 'channel-categories-filtered-empty',
  /** Top pagination control for the category mapper list. */
  categoriesPagination: 'channel-categories-pagination',
  /** Bottom pagination control for the category mapper list. */
  categoriesPaginationBottom: 'channel-categories-pagination-bottom',
  /**
   * ERP configuration view, Drift tab (TV1-2146). READ-ONLY audit of the Drift
   * Episode ⟕ ERP Sync Mirror join. The per-value and per-clock ids exist so a
   * test can assert SDD Risk R1 directly: the three values and the two clocks
   * must be THREE and TWO distinct cells, never collapsed into fewer.
   */
  erpDriftTab: 'integrations-erp-drift-tab',
  /** Drift tab: the episodes-search spinner. */
  erpDriftLoading: 'integrations-erp-drift-loading',
  /** Drift tab: "this integration has no drift at all". */
  erpDriftEmpty: 'integrations-erp-drift-empty',
  /** Drift tab: "drift exists, but the current filters hide all of it" — a DIFFERENT operator situation. */
  erpDriftFilteredEmpty: 'integrations-erp-drift-filtered-empty',
  /** Drift tab: the episode table. */
  erpDriftTable: 'integrations-erp-drift-table',
  /** Drift tab: entity filter (all / item / stock / price). */
  erpDriftEntityFilter: 'integrations-erp-drift-entity-filter',
  /** Drift tab: status filter (open / closed / all), defaulting to open. */
  erpDriftStatusFilter: 'integrations-erp-drift-status-filter',
  /** Drift tab: page control of the episodes search. */
  erpDriftPagination: 'integrations-erp-drift-pagination',
  /** One drift episode row (keyed by episode id). */
  erpDriftRow: ( episodeId: string ): string =>
    `integrations-erp-drift-row-${ episodeId }`,
  /** Scope cell (mirror warehouseId / priceListId), or the raw syncKey on a mirror miss. */
  erpDriftScope: ( episodeId: string ): string =>
    `integrations-erp-drift-scope-${ episodeId }`,
  /** Raw syncKey, rendered monospace ONLY when no mirror row joined. */
  erpDriftSyncKey: ( episodeId: string ): string =>
    `integrations-erp-drift-sync-key-${ episodeId }`,
  /** Value 1 of 3 — Teselly's value AT THE MOMENT THE EPISODE OPENED (from the episode). */
  erpDriftOurValueAtOpen: ( episodeId: string ): string =>
    `integrations-erp-drift-our-value-at-open-${ episodeId }`,
  /** Value 2 of 3 — the ERP's value AT THE MOMENT THE EPISODE OPENED (from the episode). */
  erpDriftTheirValueAtOpen: ( episodeId: string ): string =>
    `integrations-erp-drift-their-value-at-open-${ episodeId }`,
  /** Value 3 of 3 — the LAST value observed in the ERP (from the mirror, a later moment). */
  erpDriftTheirValueLastObserved: ( episodeId: string ): string =>
    `integrations-erp-drift-their-value-last-observed-${ episodeId }`,
  /** Clock 1 of 2 — when TESELLY observed the ERP (mirror `observedAt`). */
  erpDriftObservedAt: ( episodeId: string ): string =>
    `integrations-erp-drift-observed-at-${ episodeId }`,
  /** Clock 2 of 2 — when the ERP says the record was written (mirror `writeDate`). */
  erpDriftWriteDate: ( episodeId: string ): string =>
    `integrations-erp-drift-write-date-${ episodeId }`,
  /** Provenance cell: externalProductId / externalScopeId / currencyIsoCode. */
  erpDriftProvenance: ( episodeId: string ): string =>
    `integrations-erp-drift-provenance-${ episodeId }`,
  /** Since / duration cell: openedAt plus open-for, or durationMs once closed. */
  erpDriftSince: ( episodeId: string ): string =>
    `integrations-erp-drift-since-${ episodeId }`,
  /** Open / closed badge (Mantine semantic colours only — no new hex). */
  erpDriftStatusBadge: ( episodeId: string ): string =>
    `integrations-erp-drift-status-${ episodeId }`,
  /** Per-row re-check button — rendered only under `sales-channels:integrations:configure`. */
  erpDriftRecheckButton: ( episodeId: string ): string =>
    `integrations-erp-drift-recheck-${ episodeId }`,
  /** Verdict of the last re-check, shown ON the row (SDD Risk R2), not only in a toast. */
  erpDriftRecheckOutcome: ( episodeId: string ): string =>
    `integrations-erp-drift-recheck-outcome-${ episodeId }`
} as const;
